import React, {
  type CSSProperties,
  type FC,
  type HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Add, DragHandle } from '@mui/icons-material';
import { Chip, IconButton, Stack } from '@mui/material';
import { CircleX } from 'lucide-react';

import Button from '../../../common/Button';
import FormInput from '../../form/FormInput';

type AircraftData = {
  aircraft: string;
  airports: string[];
};

type ItemProps = HTMLAttributes<HTMLDivElement> & {
  id: string;
  withOpacity?: boolean;
  isDragging?: boolean;
  value: string;
  handleClearValue: (value: string) => void;
};

type EditAircraftFormProps = {
  defaultValues: AircraftData;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  handlePrimaryAction: (data: AircraftData & { origin: string }) => void;
  handleSecondaryAction?: () => void;
};

export const AircraftAirportSortableItem: FC<ItemProps> = (props) => {
  const { value, id, handleClearValue } = props;

  const {
    isDragging,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style: CSSProperties = {
    opacity: isDragging ? '0.5' : '1',
    borderRadius: '100px',
    cursor: isDragging ? 'grabbing' : 'grab',
    backgroundColor: '#ffffff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Chip
        key={value}
        tabIndex={undefined}
        label={
          <div className="flex items-center gap-[6px]">
            <DragHandle
              {...attributes}
              {...listeners}
              className="cursor-grab"
            />
            <p className="h-full py-1 cursor-text">{value}</p>
            <CircleX
              className="cursor-pointer"
              onClick={() => {
                handleClearValue(value);
              }}
              width={20}
              height={20}
            />
          </div>
        }
        style={{
          fontSize: '16px',
          backgroundColor: '#01285f ',
          color: '#FFF',
        }}
      />
    </div>
  );
};

const EditAircraftForm = ({
  defaultValues,
  handlePrimaryAction,
  handleSecondaryAction,
}: EditAircraftFormProps) => {
  const [airports, setAirports] = useState<string[]>([]);
  const [items, setItems] = useState<string[]>([]);

  const form = useForm({
    defaultValues: {
      aircraft: defaultValues?.aircraft,
      airport: '',
    },
  });

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const {
    handleSubmit,
    setError,
    watch,
    reset,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form;

  const formData = watch();

  useEffect(() => {
    if (errors) {
      clearErrors('airport');
    }
  }, [formData['airport']]);

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({
      aircraft: defaultValues.aircraft,
    });

    setAirports(defaultValues.airports.filter((a) => a.length > 0) ?? []);
  }, [defaultValues]);

  useEffect(() => {
    setItems(airports);
  }, [airports]);

  const newAirports = useMemo(() => {
    const airport = watch('airport');

    if (!airport) {
      return airports;
    }

    if (airports.includes(airport.trim())) {
      setError('airport', {
        message: 'This item is already added.',
      });
      return;
    }

    const commaItems = airport.split(',').map((item) => item.trim());

    const newItems = Array.from(
      new Set(commaItems.filter((item) => item))
    ).filter((item) => !item.includes(','));

    const result = Array.from(new Set([...airports, ...newItems]));

    return result;
  }, [watch('airport'), airports]);

  const onSubmit: SubmitHandler<any> = (_) => {
    const aircraft = watch('aircraft');
    const airport = watch('airport');

    const defaultAircraft = defaultValues?.aircraft;

    if (!aircraft.trim()) {
      setError('aircraft', {
        message: 'This field is required.',
      });
      return;
    }

    if (!newAirports) {
      return;
    }

    handlePrimaryAction({
      origin: defaultAircraft,
      aircraft,
      airports:
        airport.length > 0 ? newAirports : airports.length ? airports : [''],
    });
  };

  const handleAddAirport = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    const airport = watch('airport');

    if (!airport.trim()) {
      setError('airport', {
        message: 'This field is required.',
      });
      return;
    }

    if (!newAirports) {
      return;
    }

    setAirports(newAirports);
    form.setValue('airport', '');

    clearErrors('airport');
  };

  const handleClearAirport = (item: string) => {
    setAirports(airports.filter((rec) => rec !== item));
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over?.id) {
        setItems((items) => {
          const oldIndex = items.indexOf(active.id.toString());
          const newIndex = items.indexOf(over.id.toString());

          const updateAirports = arrayMove(items, oldIndex, newIndex);

          setAirports(updateAirports);

          return updateAirports;
        });
      }
    },
    [airports]
  );

  return (
    <FormProvider {...form}>
      <form className="space-y-6">
        <FormInput
          name="aircraft"
          label="Aircraft"
          labelClassName="text-[#2196F3] font-semibold"
          helperText={errors['aircraft']?.message as string}
        />
        <FormInput
          required={airports.length === 0}
          label="Airport"
          helperText={errors['airport']?.message as string}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddAirport(e);
            }
          }}
          labelClassName="text-[#2196F3] font-semibold"
          name="airport"
          endAdornment={
            <IconButton onClick={handleAddAirport} aria-label="add">
              <Add
                style={{
                  width: '16px',
                  height: '16px',
                  color: '#666666',
                }}
              />
            </IconButton>
          }
        />
        <div>
          <Stack
            direction="row"
            style={{
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={items} strategy={rectSortingStrategy}>
                {items.map((v) => {
                  if (!v) {
                    return;
                  }

                  return (
                    <AircraftAirportSortableItem
                      key={v}
                      id={v}
                      value={v}
                      handleClearValue={handleClearAirport}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </Stack>
        </div>
        <div className="flex-end flex justify-end gap-3">
          <Button
            onClick={() => {
              reset();
              handleSecondaryAction?.();
            }}
            type="button"
            variant="outlined"
            tabIndex={3}
          >
            Cancel
          </Button>
          <Button
            loading={isSubmitting}
            type="button"
            onClick={handleSubmit((event) => onSubmit(event))}
            tabIndex={2}
            variant="contained_blue"
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default EditAircraftForm;
